import { debounce } from 'lodash';
import { Dispatch } from '@reduxjs/toolkit';
import { 
  updatePizzaMeatToppings, 
  updatePizzaVeggieToppings, 
  updatePizzaFinishers,
  updatePizzaCrust,
  updatePizzaSize,
  updatePizzaSauce,
  updatePizzaCheese
} from './pizza.slice';
import { PizzaIngredient, PizzaIngredientOption, CrustOption } from '../dataTransformers/builderTypes';
import { getOrInitializeOptimizely } from '../../../../optimizely/optimizely';


const DEBOUNCE_DELAY = 50;

function makeDebouncedAction<T extends any[]>(
  originalAction: (...args: T) => any,
  debouncedFn: (dispatch: Dispatch, ...args: T) => void
) {
  const debounced = debounce(debouncedFn, DEBOUNCE_DELAY);
  
  return (...args: T) => (dispatch: Dispatch) => {
    const optimizelyInstance = getOrInitializeOptimizely();
    const isBuilderSelectionBugFixEnabled = optimizelyInstance?.isFeatureEnabled('ims-595-pizza-builder-selection-bugfix') ?? false;
    
    // Skip debouncing in test environments to prevent flaky tests
    const isTestEnvironment = process.env.NODE_ENV === 'test' || 
                             process.env.INTEGRATION_TEST_ENV === 'ci' ||
                             process.env.JEST_WORKER_ID !== undefined ||
                             typeof jest !== 'undefined';
    
    if (!isBuilderSelectionBugFixEnabled || isTestEnvironment) {
      return dispatch(originalAction(...args));
    }
    return debounced(dispatch, ...args);
  };
}

export const debouncedUpdateMeatToppings = makeDebouncedAction(
  updatePizzaMeatToppings,
  (dispatch: Dispatch, toppings: PizzaIngredient[]) => {
    dispatch(updatePizzaMeatToppings(toppings));
  }
);

export const debouncedUpdateVeggieToppings = makeDebouncedAction(
  updatePizzaVeggieToppings,
  (dispatch: Dispatch, toppings: PizzaIngredient[]) => {
    dispatch(updatePizzaVeggieToppings(toppings));
  }
);

export const debouncedUpdateFinishers = makeDebouncedAction(
  updatePizzaFinishers,
  (dispatch: Dispatch, finishers: PizzaIngredient[]) => {
    dispatch(updatePizzaFinishers(finishers));
  }
);

export const debouncedUpdateCrust = makeDebouncedAction(
  updatePizzaCrust,
  (dispatch: Dispatch, crust: CrustOption) => {
    dispatch(updatePizzaCrust(crust));
  }
);

export const debouncedUpdateSize = makeDebouncedAction(
  updatePizzaSize,
  (dispatch: Dispatch, size: PizzaIngredientOption | null) => {
    if (size === null) return;
    dispatch(updatePizzaSize(size));
  }
);

export const debouncedUpdateSauce = makeDebouncedAction(
  updatePizzaSauce,
  (dispatch: Dispatch, sauce: PizzaIngredient) => {
    dispatch(updatePizzaSauce(sauce));
  }
);

export const debouncedUpdateCheese = makeDebouncedAction(
  updatePizzaCheese,
  (dispatch: Dispatch, cheese: PizzaIngredient) => {
    dispatch(updatePizzaCheese(cheese));
  }
);